import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from "react";
import { registerOrgTokenRefreshListener } from "../services/api/authTokenBridge";
import type { AuthUser } from "../types/authUser";

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  login: (
    userData: Record<string, unknown>,
    idToken: string,
    refreshToken?: string | null
  ) => void;
  logout: () => void;
  updateUser: (updatedUser: Record<string, unknown> | ((prev: Record<string, unknown> | null) => Record<string, unknown> | null)) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize state directly from localStorage so token is available on first render.
  const [user, setUser] = useState<Record<string, unknown> | null>(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [token, setToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem("token");
    return storedToken;
  });
  const login = useCallback(
    (
      userData: Record<string, unknown>,
      idToken: string,
      refreshToken?: string | null
    ) => {
      setUser(userData);
      setToken(idToken);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("token", idToken);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      } else {
        localStorage.removeItem("refreshToken");
      }
    },
    []
  );

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
  }, []);

  const updateUser = useCallback(
    (
      updatedUser:
        | Record<string, unknown>
        | ((
            prev: Record<string, unknown> | null
          ) => Record<string, unknown> | null)
    ) => {
      if (typeof updatedUser === "function") {
        setUser((prev) => {
          const newUser = updatedUser(prev);
          if (newUser) {
            localStorage.setItem("user", JSON.stringify(newUser));
          } else {
            localStorage.removeItem("user");
          }
          return newUser;
        });
      } else {
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    },
    []
  );

  const value = useMemo(
    // login()/updateUser() intentionally stay loosely typed (Record<string,
    // unknown>) since they're fed from several different shapes (org login,
    // team login, ...); this cast is the one place that asserts what `user`
    // actually is for everything that reads it via useAuth().
    () => ({ user: user as AuthUser | null, token, login, logout, updateUser }),
    [user, token, login, logout, updateUser]
  );

  useEffect(() => {
    registerOrgTokenRefreshListener(({ idToken }) => {
      setToken(idToken);
    });
    return () => registerOrgTokenRefreshListener(null);
  }, []);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
